import { useValidationForm } from './useValidationForm';
import { useDispatch, useSelector } from 'react-redux';
import {
  EMAIL_COMPANY,
  INVOICE_ADDRESS_COMPANY,
  LOCATION_COMPANY,
  NAME_COMPANY,
  TEMPORAL_LOGO_COMPANY,
  URL_COMPANY,
  ID_COMPANY
} from '../common/adapters';
import {
  draftCompanySelector,
  draftCompanySessionUpdated,
  createCompany as createCompanyThunk,
  updateCompany as updateCompanyThunk
} from '../redux/features/company/companiesSlice';
import {
  uploadFile,
  deleteFile
} from '../redux/features/file/filesSlice';
import {
  draftFileLogoUrlSelector,
  draftFileSelector,
  isProcessingSelector
} from '../redux/features/file/filesSlice';

const schemaToValidate = {};

schemaToValidate[URL_COMPANY] = {
  presence: { allowEmpty: true },
  length: {
    maximum: 300,
    minimum: 8
  }
};

schemaToValidate[NAME_COMPANY] = {
  presence: { allowEmpty: false, message: 'is required' },
  length: {
    maximum: 200,
    minimum: 2
  }
};

schemaToValidate[EMAIL_COMPANY] = {
  presence: { allowEmpty: false, message: 'is required' },
  email: true,
  length: {
    maximum: 64
  }
};

schemaToValidate[LOCATION_COMPANY] = {
  presence: { allowEmpty: false, message: 'is required' },
  length: {
    maximum: 200,
    minimum: 2
  }
};

schemaToValidate[INVOICE_ADDRESS_COMPANY] = {
  presence: { allowEmpty: false, message: 'is required' },
  length: {
    maximum: 600,
    minimum: 2
  }
};

export const useCompanyForm = () => {
  const dispatch = useDispatch();
  const draftCompany = useSelector(draftCompanySelector);
  const draftFile = useSelector(draftFileSelector);
  const companyLogoUrl = useSelector(draftFileLogoUrlSelector);
  const isProcessingLogo = useSelector(isProcessingSelector);

  // Wrap thunks to match the expected mutation API
  const createCompany = ({ company }) => dispatch(createCompanyThunk({ company }));
  const updateCompany = ({ company }) => dispatch(updateCompanyThunk({ company }));
  const uploadFileAction = ({ file }) => dispatch(uploadFile({ file }));
  const deleteFileAction = ({ fileId }) => dispatch(deleteFile({ fileId }));
  
  const {
    handleChange,
    hasError,
    formState,
    isValidForm,
    handleChangeCustomFieldName
  } = useValidationForm({
    schemaToValidate,
    afterValidateOnChangeFn: (updatedState) => {
      dispatch(
        draftCompanySessionUpdated({
          company: updatedState
        })
      );
    },
    initValues: draftCompany
  });

  // Check if we're editing an existing company
  const isEditMode = formState.values && formState.values[ID_COMPANY];
  const handleOnUploadFile = (file) => {
    uploadFileAction({
      file
    });
  };
  const handleOnDeleteFile = (fileId) => {
    deleteFileAction({
      fileId
    });
  };

  const onDeleteLogo = () => {
    const fileId = draftFile && draftFile.id;
    if (fileId) {
      handleOnDeleteFile(fileId);
    }
  };

  const onUploadFile = ({ target }) => {
    const file = target.files[0];
    if (file) {
      const localImageUrl = window.URL.createObjectURL(file);
      handleChangeCustomFieldName(localImageUrl, TEMPORAL_LOGO_COMPANY);
      handleOnUploadFile(file);
    }
  };

  return {
    handleChange,
    handleChangeCustomFieldName,
    hasError,
    formValues: formState.values,
    formErrors: formState.errors,
    isValidForm,
    createCompany,
    updateCompany,
    onUploadFile,
    onDeleteLogo,
    file: draftFile,
    isProcessingLogo,
    companyLogoUrl,
    isEditMode
  };
};
